import { Bot, Menu, Moon, Sun, User, X } from 'lucide-react';
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useAppStore } from '../../store/useAppStore';
import SupportWidget from '../support/SupportWidget';
import ProfileDropdown from './ProfileDropdown';
import './Topbar.css';

const Topbar: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  const { theme, toggleTheme } = useAppStore();
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  return (
    <div className="topbar">
      <div className="topbar-content">
        <div className="topbar-left">
          {/* Mobile Menu Button */}
          <button
            className="mobile-menu-button"
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            aria-label="Menu"
          >
            {showMobileMenu ? <X size={24} /> : <Menu size={24} />}
          </button>

          <Link to="/" className="logo">
            <span className="logo-run">Run</span>
            <span className="logo-ai">AI</span>
          </Link>
          <div className="topbar-divider" />
          <Link to="/research" className="topbar-nav-link desktop-only">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
              <line x1="10" y1="9" x2="8" y2="9"/>
            </svg>
            <span>Research</span>
          </Link>
          <Link to="/social" className="topbar-nav-link desktop-only">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
            <span>Social</span>
          </Link>
          <Link to="/pricing" className="topbar-nav-link desktop-only">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="1" x2="12" y2="23" />
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
            </svg>
            <span>Pricing</span>
          </Link>
        </div>
        {/* Search bar kaldirildi — tasma sorunu vardi */}
        <div className="topbar-center desktop-only" />
        <div className="topbar-right">
          <Link to="/ask-ai" className="topbar-icon desktop-only" title="AskAI">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"/>
            </svg>
          </Link>
          {/* Social - sadece aiworker/both/admin */}
          {user && ['aiworker', 'both', 'admin'].includes(user.role) && (
            <Link to="/social" className="topbar-icon desktop-only" title="RunAI Social">
              <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                <User size={16} />
                <Bot size={16} />
              </div>
            </Link>
          )}
          {/* FlzHub - herkese gorunur */}
          <button
            className="topbar-icon desktop-only"
            title="FlzHub Chat"
            onClick={() => {
              if (user?.role === 'admin') {
                navigate('/flzhub/chat')
              } else {
                navigate('/flzhub/chat')
              }
            }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
            </svg>
          </button>


          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className="topbar-icon desktop-only"
            title={theme === 'dark' ? 'Switch to Light' : 'Switch to Dark'}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--text-secondary)' }}
          >
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </button>

          {isAuthenticated && user ? (
            <>
              <ProfileDropdown user={user as any} />
            </>
          ) : (
            <Link to="/login" className="btn-primary">Login</Link>
          )}
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {showMobileMenu && (
        <>
          <div
            className={`mobile-menu-overlay ${showMobileMenu ? 'active' : ''}`}
            onClick={() => setShowMobileMenu(false)}
          />
          <div className={`mobile-menu ${showMobileMenu ? 'active' : ''}`}>
            <div className="mobile-menu-header">
              <span className="mobile-menu-title">Menu</span>
              <button
                className="mobile-menu-close"
                onClick={() => setShowMobileMenu(false)}
                aria-label="Close menu"
              >
                <X size={24} />
              </button>
            </div>

            <div className="mobile-menu-content">
              {/* Main Navigation */}
              <div className="mobile-menu-section">
                <Link to="/" className="mobile-menu-item" onClick={() => setShowMobileMenu(false)}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                    <polyline points="9 22 9 12 15 12 15 22"/>
                  </svg>
                  <span>Landing Page</span>
                </Link>
                <Link to="/research" className="mobile-menu-item" onClick={() => setShowMobileMenu(false)}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/>
                    <polyline points="14 2 14 8 20 8"/>
                  </svg>
                  <span>Research</span>
                </Link>
                <Link to="/projects-listing" className="mobile-menu-item" onClick={() => setShowMobileMenu(false)}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
                  </svg>
                  <span>Projects</span>
                </Link>
                {/* Social - sadece aiworker/both/admin */}
                {user && ['aiworker', 'both', 'admin'].includes(user.role) && (
                  <Link to="/social" className="mobile-menu-item" onClick={() => setShowMobileMenu(false)}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <User size={18} />
                      <Bot size={18} />
                    </div>
                    <span>Social Feed</span>
                  </Link>
                )}
                {/* FlzHub - herkese gorunur, admin girebilir */}
                <button
                  className="mobile-menu-item"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', width: '100%', textAlign: 'left' }}
                  onClick={() => {
                    setShowMobileMenu(false);
                    navigate('/flzhub/chat');
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                  </svg>
                  <span>FlzHub Chat</span>
                </button>
                <Link to="/ask-ai" className="mobile-menu-item" onClick={() => setShowMobileMenu(false)}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"/>
                  </svg>
                  <span>AskAI</span>
                </Link>
                <Link to="/aicloud" className="mobile-menu-item" onClick={() => setShowMobileMenu(false)}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z"/>
                  </svg>
                  <span>AiCloud</span>
                </Link>
              </div>

              {/* Stats */}
              <div className="mobile-menu-section">
                <div className="mobile-menu-stats">
                  {/* Stats removed temporarily */}
                </div>
              </div>

              {/* User Actions */}
              {isAuthenticated && user ? (
                <div className="mobile-menu-section">
                  <div className="mobile-menu-user">
                    <div className="mobile-menu-user-avatar">
                      {user.display_name?.charAt(0).toUpperCase() || 'U'}
                    </div>
                    <div className="mobile-menu-user-info">
                      <div className="mobile-menu-user-name">{user.display_name}</div>
                      <div className="mobile-menu-user-email">{user.email}</div>
                    </div>
                  </div>


                  <Link to={`/profile/${user.username || user.id}`} className="mobile-menu-item" onClick={() => setShowMobileMenu(false)}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                    <span>Profile</span>
                  </Link>

                  <Link to="/my-projects" className="mobile-menu-item" onClick={() => setShowMobileMenu(false)}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
                    </svg>
                    <span>My Projects</span>
                  </Link>

                  <Link to="/my-agents" className="mobile-menu-item" onClick={() => setShowMobileMenu(false)}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="3" />
                      <path d="M12 1v6m0 6v6" />
                    </svg>
                    <span>My Agents</span>
                  </Link>

                  <Link to="/wallet" className="mobile-menu-item" onClick={() => setShowMobileMenu(false)}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                      <line x1="1" y1="10" x2="23" y2="10" />
                    </svg>
                    <span>Wallet</span>
                  </Link>

                  <Link to="/support" className="mobile-menu-item" onClick={() => setShowMobileMenu(false)}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                      <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                    <span>Support</span>
                  </Link>

                  <Link to="/settings" className="mobile-menu-item" onClick={() => setShowMobileMenu(false)}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="3" />
                      <path d="M12 1v6m0 6v6" />
                    </svg>
                    <span>Settings</span>
                  </Link>

                  {/* Admin Panel — WebOS Dock'tan açılır, burada link yok */}

                  <button
                    className="mobile-menu-item mobile-menu-logout"
                    onClick={() => {
                      logout();
                      setShowMobileMenu(false);
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                      <polyline points="16 17 21 12 16 7" />
                      <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                    <span>Logout</span>
                  </button>
                </div>
              ) : (
                <div className="mobile-menu-section">
                  <Link
                    to="/login"
                    className="mobile-menu-button"
                    onClick={() => setShowMobileMenu(false)}
                  >
                    Login
                  </Link>
                  <Link
                    to="/register"
                    className="mobile-menu-button mobile-menu-button-secondary"
                    onClick={() => setShowMobileMenu(false)}
                  >
                    Register
                  </Link>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Support Widget */}
      <SupportWidget />
    </div>
  );
};

export default Topbar;
